import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiX } = FiIcons;

const AddRowModal = ({ structure, onSave, onClose }) => {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getInputType = (columnType) => {
    if (columnType.includes('int') || columnType.includes('decimal') || columnType.includes('float')) {
      return 'number';
    }
    if (columnType.includes('date')) {
      return 'date';
    }
    if (columnType.includes('time')) {
      return 'time';
    }
    if (columnType.includes('datetime') || columnType.includes('timestamp')) {
      return 'datetime-local';
    }
    return 'text';
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">Add New Row</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-4">
            {structure.map((column) => (
              <div key={column.Field}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {column.Field}
                  {column.Key === 'PRI' && column.Extra === 'auto_increment' && (
                    <span className="ml-1 text-blue-500 text-xs">(Auto)</span>
                  )}
                  {column.Null === 'NO' && column.Extra !== 'auto_increment' && (
                    <span className="ml-1 text-red-500 text-xs">*</span>
                  )}
                </label>
                <div className="text-xs text-gray-500 mb-1">{column.Type}</div>
                
                {column.Key === 'PRI' && column.Extra === 'auto_increment' ? (
                  <input
                    type="text"
                    value="Auto-generated"
                    disabled
                    className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
                  />
                ) : column.Type.includes('text') || column.Type.includes('longtext') ? (
                  <textarea
                    value={formData[column.Field] || ''}
                    onChange={(e) => handleInputChange(column.Field, e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    required={column.Null === 'NO'}
                  />
                ) : (
                  <input
                    type={getInputType(column.Type)}
                    value={formData[column.Field] || ''}
                    onChange={(e) => handleInputChange(column.Field, e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required={column.Null === 'NO'}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center"
            >
              <SafeIcon icon={FiPlus} className="mr-2" />
              {saving ? 'Adding...' : 'Add Row'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default AddRowModal;